<div class="page-container">
  <!-- Search Section -->
  <div class="search-container">
    <div class="search-controls">
      <!-- Search Input -->
      <mat-form-field appearance="outline" class="search-field">
        <mat-label>Search License Plate</mat-label>
        <input matInput
          [(ngModel)]="licensePlateSearch"
          placeholder="Enter license plate"
          [disabled]="isLoading">
        <mat-icon matSuffix>directions_car</mat-icon>
      </mat-form-field>

      <!-- Search Button -->
      <button mat-raised-button
        color="primary"
        (click)="applyFilters()"
        [disabled]="isLoading"
        class="search-button">
        <mat-icon>search</mat-icon>
        Search
      </button>

      <!-- Clear Search Button -->
      <button mat-stroked-button
        (click)="clearFilters()"
        [disabled]="isLoading"
        class="clear-search-button">
        <mat-icon>clear</mat-icon>
        Clear
      </button>
    </div>
  </div>

  <!-- Filters Section -->
  <div class="filters-container">
    <div class="filters-header" (click)="toggleFilters()">
      <div class="filters-title">
        <mat-icon>filter_list</mat-icon>
        <span>Filters</span>
        @if (getActiveFiltersCount() > 0) {
          <mat-chip-set class="filter-count">
            <mat-chip>{{ getActiveFiltersCount() }} active</mat-chip>
          </mat-chip-set>
        }
      </div>
      <mat-icon class="expand-icon" [class.expanded]="filtersExpanded">expand_more</mat-icon>
    </div>

    <div class="filters-content" [class.expanded]="filtersExpanded">
      <div class="filters-grid">
        <!-- Multi-Select Sensor ID Filter -->
        <div class="filter-group multiple-select">
          <label class="filter-label">Sensor IDs</label>
          <mat-form-field appearance="outline">
            <mat-label>Sensor IDs</mat-label>
            <mat-select [(value)]="selectedSensorIds" multiple [disabled]="isLoading">
              @for (sensor of availableSensors; track sensor) {
                <mat-option [value]="sensor.id">
                  {{ sensor.name }}
                </mat-option>
              }
            </mat-select>
            @if (selectedSensorIds.length > 0) {
              <mat-hint>
                {{ selectedSensorIds.length }} sensor(s) selected
              </mat-hint>
            }
          </mat-form-field>
        </div>

        <div class="filter-group multiple-select">
          <label class="filter-label">Actions</label>
          <mat-form-field appearance="outline">
            <mat-label>Actions</mat-label>
            <mat-select [(value)]="selectedActions" multiple [disabled]="isLoading">
              @for (action of allActions; track action) {
                <mat-option [value]="action">
                  {{ action }}
                </mat-option>
              }
            </mat-select>
            @if (selectedSensorIds.length > 0) {
              <mat-hint>
                {{ selectedSensorIds.length }} sensor(s) selected
              </mat-hint>
            }
          </mat-form-field>
        </div>

        <!-- Start DateTime Filter -->
        <div class="filter-group">
          <label class="filter-label">Start Date & Time</label>
          <div class="datetime-group">
            <mat-form-field appearance="outline" class="date-field">
              <mat-label>Start Date</mat-label>
              <input matInput
                [matDatepicker]="startPicker"
                [(ngModel)]="startDate"
                placeholder="Select start date"
                [disabled]="isLoading">
              <mat-datepicker-toggle matSuffix [for]="startPicker"></mat-datepicker-toggle>
              <mat-datepicker #startPicker></mat-datepicker>
            </mat-form-field>
            <mat-form-field appearance="outline" class="time-field">
              <mat-label>Time</mat-label>
              <input matInput
                type="time"
                [(ngModel)]="startTime"
                [disabled]="isLoading">
            </mat-form-field>
          </div>
        </div>

        <!-- End DateTime Filter -->
        <div class="filter-group">
          <label class="filter-label">End Date & Time</label>
          <div class="datetime-group">
            <mat-form-field appearance="outline" class="date-field">
              <mat-label>End Date</mat-label>
              <input matInput
                [matDatepicker]="endPicker"
                [(ngModel)]="endDate"
                placeholder="Select end date"
                [disabled]="isLoading">
              <mat-datepicker-toggle matSuffix [for]="endPicker"></mat-datepicker-toggle>
              <mat-datepicker #endPicker></mat-datepicker>
            </mat-form-field>
            <mat-form-field appearance="outline" class="time-field">
              <mat-label>Time</mat-label>
              <input matInput
                type="time"
                [(ngModel)]="endTime"
                [disabled]="isLoading">
            </mat-form-field>
          </div>
        </div>
      </div>

      <!-- Filter Actions -->
      <div class="filter-actions">
        <button mat-raised-button color="primary" (click)="applyFilters()" [disabled]="isLoading">
          <mat-icon>search</mat-icon>
          Apply Filters
        </button>
        <button mat-stroked-button (click)="clearFilters()" [disabled]="isLoading">
          <mat-icon>clear</mat-icon>
          Clear Filters
        </button>
      </div>
    </div>
  </div>

  <!-- Results Section -->
  <div class="results-section">
    <mat-paginator
      #paginator
      [length]="totalData"
      [pageSizeOptions]="pageSizes"
      showFirstLastButtons
      [disabled]="isLoading"
    ></mat-paginator>

    <div class="overflow-auto">
      <table mat-table matSort [dataSource]="dataSource" class="results-table mat-elevation-z2">

        <!-- Created Column -->
        <ng-container matColumnDef="created">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Created</th>
          <td mat-cell *matCellDef="let log">{{ $safeNavigationMigration(log?.created) | date: 'yyyy-MM-dd HH:mm:ss' }}</td>
        </ng-container>

        <!-- Plate Text Column -->
        <ng-container matColumnDef="plateText">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>License Plate</th>
          <td mat-cell *matCellDef="let log">
            <span class="plate-text">{{ log.definedData?.plateText }}</span>
          </td>
        </ng-container>

        <!-- DateTime Column -->
        <ng-container matColumnDef="datetime">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Detection Time</th>
          <td mat-cell *matCellDef="let log">{{ log.definedData?.datetime }}</td>
        </ng-container>

        <!-- Camera Serial Number Column -->
        <ng-container matColumnDef="cameraSerialNumber">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Camera Serial</th>
          <td mat-cell *matCellDef="let log">{{ log.definedData?.cameraSerialNumber }}</td>
        </ng-container>

        <!-- Camera IP Address Column -->
        <ng-container matColumnDef="cameraIPAddress">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Camera IP</th>
          <td mat-cell *matCellDef="let log">{{ log.definedData?.cameraIPAddress }}</td>
        </ng-container>

        <!-- Sensor Provider ID Column -->
        <ng-container matColumnDef="sensorProviderID">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Sensor Provider</th>
          <td mat-cell *matCellDef="let log">{{ log.definedData?.sensorProviderID }}</td>
        </ng-container>

        <!-- Action Column -->
        <ng-container matColumnDef="action">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Action</th>
          <td mat-cell *matCellDef="let log">{{ log?.action }}</td>
        </ng-container>

        <!-- Stage Column -->
        <ng-container matColumnDef="stage">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Stage</th>
          <td mat-cell *matCellDef="let log" [class.center-dash]="!log?.stage">{{ log?.stage ? log?.stage : '-' }}</td>
        </ng-container>

        <!-- Handled At Column -->
        <ng-container matColumnDef="handledAt">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Handled At</th>
          <td mat-cell *matCellDef="let log" [class.center-dash]="!log?.handledAt">{{ log?.handledAt ? (log?.handledAt | date: 'yyyy-MM-dd HH:mm:ss') : '-' }}</td>
        </ng-container>

        <!-- Handled By Event ID Column -->
        <ng-container matColumnDef="handledByEventId">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>Handled By Event ID</th>
          <td mat-cell *matCellDef="let log" [class.center-dash]="!log?.handledByEventId">{{ log?.handledByEventId ? log?.handledByEventId : '-' }}</td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-row *matRowDef="let log; columns: displayedColumns;" (click)="goToLPRLogDetails(log)"></tr>

        <tr class="mat-row" *matNoDataRow>
          <td class="mat-cell no-data" [attr.colspan]="displayedColumns.length">
            <div class="no-data-content">
              <mat-icon>search_off</mat-icon>
              <span>No LPR logs found</span>
            </div>
          </td>
        </tr>
      </table>
    </div>
  </div>
</div>
